import { ReactNode, useId, useMemo, useState } from 'react';
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form';
import * as Combobox from '@givebutter/primitives/combobox';
import { Plurality } from '@givebutter/util';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormField } from 'components/form/FormField';
import { FormSelectOption } from 'components/form/FormSelect';

export interface FormComboboxOption extends FormSelectOption {
  searchTerms?: string[];
}

interface FormComboboxProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label?: ReactNode;
  description?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  options: FormComboboxOption[];
  disabled?: boolean;
  resource?: string;
  plurality?: Plurality;
}

export const FormCombobox = <T extends FieldValues>({
  name,
  control,
  label,
  description,
  placeholder,
  searchPlaceholder,
  options,
  disabled,
  resource,
  plurality,
}: FormComboboxProps<T>) => {
  const labelId = useId();
  const errorId = useId();
  const [query, setQuery] = useState('');
  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({ name, control });

  const selected = value === '' || value == null ? [] : [String(value)];

  const visibleOptions = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) {
      return options;
    }
    return options.filter(opt => {
      const haystack = [opt.label, ...(opt.searchTerms ?? [])].join(' ').toLowerCase();
      return terms.every(term => haystack.includes(term));
    });
  }, [options, query]);

  return (
    <FormField
      label={label}
      labelId={label ? labelId : undefined}
      description={description}
      error={error?.message}
      errorId={errorId}
    >
      <Combobox.Root
        allowMultiple={false}
        value={selected}
        onChangeValue={next => {
          onChange(next[0] ?? '');
          onBlur();
        }}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setQuery('');
          }
        }}
        isError={!!error}
        ariaDescribedBy={error ? errorId : undefined}
        disabled={disabled}
        resource={resource}
        plurality={plurality}
      >
        <Combobox.Trigger
          aria-labelledby={label ? labelId : undefined}
          aria-label={label ? undefined : placeholder}
        >
          <Combobox.Select>
            <Combobox.SelectValue
              placeholder={placeholder ?? 'Select…'}
              className="min-w-0 truncate"
              placeholderClassName="min-w-0 truncate"
            />
            <FontAwesomeIcon icon={faChevronDown} className="size-3.5 shrink-0 text-gray-400" />
          </Combobox.Select>
        </Combobox.Trigger>
        <Combobox.Popover>
          <Combobox.Search
            value={query}
            onChangeValue={setQuery}
            placeholder={searchPlaceholder ?? 'Search'}
          />
          <Combobox.Items>
            {visibleOptions.length === 0 && <Combobox.NoneFoundText />}
            {visibleOptions.map(opt => (
              <Combobox.Item key={opt.value} value={opt.value}>
                <Combobox.ItemText className="flex min-w-0 flex-1 items-center gap-1.5">
                  {opt.icon}
                  {opt.label}
                </Combobox.ItemText>
                <Combobox.CheckmarkIndicator />
              </Combobox.Item>
            ))}
          </Combobox.Items>
        </Combobox.Popover>
      </Combobox.Root>
    </FormField>
  );
};
